import React, { useState, useEffect, useRef, useMemo, useCallback, useContext, } from "react";
const HIDE_DELAY = 500;
const REFRESH_INTERVAL = 120;
const OverPageLoadingBarContext = React.createContext(null);
export const OverPageLoadingBarProvider = ({ children }) => {
    const [isLoadingCounter, setIsLoadingCounter] = useState(0);
    const setIsLoading = useCallback((isLoading) => {
        if (isLoading) {
            setIsLoadingCounter(isLoadingCounter + 1);
        }
        else {
            setIsLoadingCounter(Math.max(isLoadingCounter - 1, 0));
        }
    }, [isLoadingCounter, setIsLoadingCounter]);
    const isLoading = useMemo(() => isLoadingCounter !== 0, [isLoadingCounter]);
    return (React.createElement(OverPageLoadingBarContext.Provider, { value: [isLoading, setIsLoading] }, children));
};
export const useSetOverPageLoadingBarState = () => {
    const state = useContext(OverPageLoadingBarContext);
    if (!state) {
        throw new Error("OverPageLoadingBarContext.Provider not found");
    }
    const [, setLoading] = state;
    return setLoading;
};
export const LoadingBar = ({ speed = 0.9, isLoading, overPage = false, presetPercent = 0, }) => {
    const [percent, setPercent] = useState(presetPercent);
    const [shouldLoad, setShouldLoad] = useState(false);
    const intervalRef = useRef(null);
    useEffect(() => {
        if (shouldLoad) {
            intervalRef.current = setInterval(() => {
                if (percent < 99) {
                    setPercent(percent + 1);
                }
            }, REFRESH_INTERVAL);
        }
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [setPercent, shouldLoad, percent]);
    const stop = useCallback(() => {
        setShouldLoad(false);
        setPercent(100);
        const timeout = setTimeout(() => {
            setPercent(0);
        }, HIDE_DELAY);
        return () => clearInterval(timeout);
    }, [setPercent, setShouldLoad]);
    useEffect(() => {
        if (isLoading) {
            setShouldLoad(true);
        }
        else if (shouldLoad) {
            stop();
        }
    }, [stop, isLoading, shouldLoad, setShouldLoad]);
    const containerStyle = useMemo(() => ({
        opacity: percent < 100 ? 1 : 0,
        WebkitTransition: `${speed}s opacity`,
        transition: `${speed}s opacity`,
        WebkitTransitionDelay: `${percent < 100 ? 0 : HIDE_DELAY}s`,
        transitionDelay: `${percent < 100 ? 0 : HIDE_DELAY}s`,
    }), [percent, speed]);
    const barStyle = useMemo(() => ({
        width: `${percent}%`,
        WebkitTransition: `${speed}s width, ${speed}s background-color`,
        transition: `${speed}s width, ${speed}s background-color`,
    }), [percent, speed]);
    return (React.createElement("div", { className: `LoadingBar-container ${overPage ? "LoadingBar-container-overpage" : ""}`, style: containerStyle },
        React.createElement("div", { className: `LoadingBar-bar ${percent === 0 ? "LoadingBar-bar-fading" : ""} ${overPage ? "LoadingBar-bar-overpage" : ""}`, style: barStyle })));
};
export const OverPageLoadingBar = ({ speed = 0.9 }) => {
    const state = useContext(OverPageLoadingBarContext);
    if (!state) {
        throw new Error("AlertContext.Provider not found");
    }
    const [isLoading] = state;
    return React.createElement(LoadingBar, { isLoading: isLoading, speed: speed, overPage: true });
};
